import { useEffect, useState } from "react";
import { Loader2, Power } from "lucide-react";
import { DashShell } from "@/components/dash/DashShell";
import { ADMIN_NAV } from "./nav";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { api, inr, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function AdminServices() {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [edits, setEdits] = useState({});

    const load = () => { setLoading(true); api.get("/admin/services").then(({ data }) => setServices(data)).finally(() => setLoading(false)); };
    useEffect(load, []);

    const save = async (slug) => {
        const patch = edits[slug];
        if (!patch) return;
        try {
            await api.patch(`/admin/services/${slug}`, { ...patch, starting_price: patch.starting_price != null ? Number(patch.starting_price) : undefined });
            toast.success("Service updated — pricing updates everywhere automatically");
            setEdits((e) => { const n = { ...e }; delete n[slug]; return n; });
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const toggle = async (slug, active) => {
        try { await api.patch(`/admin/services/${slug}`, { active }); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const setE = (slug, k, v) => setEdits((e) => ({ ...e, [slug]: { ...e[slug], [k]: v } }));

    return (
        <DashShell nav={ADMIN_NAV} title="Service Management">
            <p className="text-sm text-teal-900/60 mb-4">Edit prices and status here — changes reflect across the entire website automatically. No code needed.</p>
            {loading ? <div className="grid place-items-center py-24 text-teal-900/40"><Loader2 className="w-7 h-7 animate-spin" /></div> : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {services.map((s) => {
                        const e = edits[s.slug] || {};
                        return (
                            <div key={s.slug} className={`bg-white rounded-2xl border border-black/5 p-5 shadow-sm ${!s.active ? "opacity-60" : ""}`} data-testid={`admin-service-${s.slug}`}>
                                <div className="flex items-start justify-between">
                                    <div><h3 className="font-semibold text-teal-900">{s.name}</h3><span className="text-xs text-teal-900/40 font-mono">{s.slug}</span></div>
                                    <div className="flex items-center gap-2"><span className="text-xs text-teal-900/50">Active</span><Switch checked={s.active} onCheckedChange={(v) => toggle(s.slug, v)} data-testid={`service-toggle-${s.slug}`} /></div>
                                </div>
                                <div className="mt-4 grid grid-cols-2 gap-3">
                                    <div><label className="text-xs text-teal-900/60">Starting Price (₹)</label><Input type="number" className="mt-1" defaultValue={s.starting_price} onChange={(ev) => setE(s.slug, "starting_price", ev.target.value)} data-testid={`service-price-${s.slug}`} /></div>
                                    <div><label className="text-xs text-teal-900/60">Current</label><div className="mt-1 h-9 flex items-center font-mono font-bold text-teal-900">{inr(s.starting_price)}</div></div>
                                </div>
                                <div className="mt-3"><label className="text-xs text-teal-900/60">Tagline</label><Input className="mt-1" defaultValue={s.tagline} onChange={(ev) => setE(s.slug, "tagline", ev.target.value)} data-testid={`service-tagline-${s.slug}`} /></div>
                                <button onClick={() => save(s.slug)} disabled={!Object.keys(e).length} className="btn-gold rounded-full px-5 py-2 text-sm mt-4 disabled:opacity-40" data-testid={`service-save-${s.slug}`}>Save Changes</button>
                            </div>
                        );
                    })}
                </div>
            )}
        </DashShell>
    );
}
